import { assign, createMachine } from 'xstate'
import { TestNode } from '../../typings'
import * as guards from './content-navigator.guards'
// import { pongMachineFactory } from '../pings'

export type ContentNavigatorMachineContext<
  TTestNode extends TestNode<TContent, TAnswerContent>,
  TContent,
  TAnswerContent,
  TSelectedAnswer
> = {
  contents: TTestNode[]
  currentIndex: number
  selectedAnswer: TSelectedAnswer | null
  answerHistory: Record<string, TSelectedAnswer>
}

export type ContentNavigatorMachineEvent<
  TTestNode extends TestNode<TContent, TAnswerContent>,
  TContent,
  TAnswerContent,
  TSelectedAnswer
> =
  | { type: 'NEXT'; answerHistory?: Record<string, TSelectedAnswer> }
  | { type: 'PREVIOUS' }
  | { type: 'ANSWER_QUESTION'; selectedAnswer: TSelectedAnswer }
  | { type: 'APPEND_CONTENT'; content: TTestNode[] }

export const createTestMachine = <
  TTestNode extends TestNode<TContent, TAnswerContent>,
  TContent,
  TAnswerContent,
  TSelectedAnswer
>(
  contents: Array<TTestNode>,
  startIndex = -1
) => {
  return createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QBc7ILIEMDGALAlgHZgB0ADgE5gBu+A9gK6wDCdhq7JEY2VAtmHasG7MBQDEAbQAMAXUSgydWPmT1CCkAA9EARgAcATgDsJfQBZj+6Ud0Amc7oDMxgDQgAnogC0TkrqtdXUMgpxcANnCHOwBfGPdUWAwcAmJyKlpGFjYOZBIqCHwqbGQAFTpWUXYpOU0lFTU2TR0EOxtpMwBWAOcnQ07ww2k3Tz19fRJ+w0Nw80jpcONDO064hLQsPCJSShp6Jkrc-MginjKKnMFkKV15JBB61XVmxDtdGxJh6XNpBydzTqdEZeBBOcKdEjmMG6cwmcxQ4x2WLxECJZJbNLELTIQ5XEhEXhgARCRiiCQyO6KZRPJr3FrTDrGYxQ4KAmx2QbuEH6CEDIJWaRg2HGaSrFFozapUhYnGXTgFU4lcq46oUurUxoaOmIQxzSHjTp2frDWa6rmvEUkIFBN7BYzhBYctaojYpbYkGUqvIK4rnL03SkPDXPbUIQxgyGOSKLfThAL6XTm0F6wW-JxIoGC8JOZ0St2YsDYr3HQq+5Vy66SOyBx6al5h-6fEwG-R9Oz28xJ8wcz6dOZvebSdli9ZJSXuz0VkuKv0VqROGvB2mgekOSGI2O6B1Tcz6JPdOyTQVhIezfT2wy510Y0gQOjYBjE64AOQAogANUpq+61kMrvRIuYZhvIB8JDEYSbBIYVrLLCzbZv8kRXmO+a3vej5XOIAAKABKr4AGoAJIAPIAKoAMrflSDR-toejBH4goGOE+gOACTiGkm3gJiQRqGqKIqDDCATIeiUrpDgajYGA6B0NwEklPg0kAIoMGg6jiG+n5UUGNHLnRCBbruZg-IC8L2FMXbpiQkTGOmsydAydg5uK17iZQklKTJck7BQnkqWpSQabhBEkRROm-vpLQwixvGsb8W4uNIhitl2QHguCPJvAMQJGqJ45pB5inSbJ8lFVJYCqepbDiAAgs+5EAOqvjhAD6ymka+5GlCRz4RUuWr-oZ-ymI5AzWG0LisYY+7GLoNkCvY-x2AmLmjmJ7rlV5pW+f5YAAGJgJAABGOAANbYXhRFkZRtQ-gN9YBN8kyWBeRj9MyTj7jyJDpksTHboaxj5ahCkVTtYNeYdJ3nZpH5fnd1E0oNBkGOGNmGp0NgJjY8KQdMMEONMVgIXM4QgzeJAAI6BZqcPaYjunI-WmU2eMThBO8xjdFu+MQoiASwTyurghT4k09VhB1Q1zVtR1XU9cRfWM5FKMtIah4cW0QKZdzwz7jCkKwZ0HOijMuhi+6EtBTVtVYVhr7PgAIq1zBK6UjsI4uelq4gQOfM5IRLAm7b9EmfGQtIW4LDYfTWnEKKED58D3HmN7qj79aWEBWsCbrWPAj4K3hHFKV2VHMawpbhUZPs2RVMgGfM6GATgvqFjJX0KXOVxwQTFYZdRibDqxtXvl7FkxbcIST7CGSTd1i3q3tz84bTKxX2jAg3ic6X57fEyRozMi60FePmQHFOPpnOWDcL7R0UWNBu4WG0c2-KKSZt+2TisQElhGihGPD0hZZR33upnUM+84rZTaEiEwnEt47w+OeVi4IHBAkEiOF0KFKaTgbviQgM8rhz1QBQe+UUdQGBgbofi8DuZ2C4m8X6VgOTQmsLQpY5hgH4KONfJUFxwFI0XkNXUh5HCwgHDYNGjCt60I6MEZwmDh59gGMAtErA+BkAADZgFQBAChvsGzzRcCEFw-xHABC-s4Xi6YoTdhFMaS8rlcHiTvA+J8hjHpOEFDZWYQlf6zHeHuLeHIJjphtO8aYu5HDAK2iVHyXiW5HxguND+U0jRJgsb9NeAQ2J9wWHEvyxVvJlWKRVKqNsUaq28Q6fwyxBhhF3LHKy80jC-zhLCb4rEil7QhvEg6R0ICnWwGdJJQ0DBLCtBxVsPjuw8lSlvAEEI+i-yMAma0JhgHWxEUzXZLRswlyHOybMhotw9y3l3SYb8fHMkDs4uIQA */
      tsTypes: {} as import('./content-navigator.machine.typegen').Typegen0,
      schema: {
        context: {} as ContentNavigatorMachineContext<TTestNode, TContent, TAnswerContent, TSelectedAnswer>,
        events: {} as ContentNavigatorMachineEvent<TTestNode, TContent, TAnswerContent, TSelectedAnswer>,
      },
      id: 'testMachine',
      context: {
        contents,
        currentIndex: startIndex,
        selectedAnswer: null,
        answerHistory: {},
      },
      initial: 'nextContent',
      states: {
        previousContent: {
          initial: 'decrementCounter',
          states: {
            decrementCounter: {
              entry: ['goToPreviousContent'],
              always: 'redirectToContent',
            },
            redirectToContent: {
              always: [
                {
                  target: '#testMachine.document',
                  cond: 'isDocument',
                },
                {
                  target: '#testMachine.practiceMode.practiceFeedback',
                  cond: 'isPractice',
                },
              ],
            },
          },
        },
        nextContent: {
          initial: 'incrementCounter',
          states: {
            incrementCounter: {
              entry: ['goToNextContent'],
              always: 'redirectToContent',
            },
            redirectToContent: {
              always: [
                {
                  target: '#testMachine.document',
                  cond: 'isDocument',
                },
                {
                  target: '#testMachine.practiceMode',
                  cond: 'isPractice',
                },
                {
                  target: '#testMachine.question',
                  cond: 'isQuestion',
                },
                {
                  target: '#testMachine.testCompleted',
                  cond: 'isLastContent',
                },
              ],
            },
          },
        },
        testCompleted: {
          type: 'final',
        },
        document: {
          tags: ['contentNode'],
          // ...invokePingMachine('document'),
          on: {
            NEXT: 'nextContent',
            PREVIOUS: {
              target: 'previousContent',
              cond: 'isPreviousContentNotQuestion',
            },
          },
        },
        practiceMode: {
          tags: ['contentNode'],
          // ...invokePingMachine('practice'),
          initial: 'practiceQuestion',
          states: {
            practiceQuestion: {
              entry: ['resetSelectedAnswer'],
              on: {
                NEXT: { target: 'practiceFeedback', actions: ['updateAnswerHistory'] },
                PREVIOUS: '#testMachine.previousContent',
                ANSWER_QUESTION: {
                  actions: ['answerQuestion'],
                },
              },
            },
            practiceFeedback: {
              on: {
                PREVIOUS: 'practiceQuestion',
                NEXT: '#testMachine.nextContent',
              },
            },
          },
        },
        question: {
          tags: ['contentNode'],
          // ...invokePingMachine('question'),
          entry: ['resetSelectedAnswer'],
          on: {
            NEXT: {
              target: 'nextContent',
              cond: 'hasAnswered',
              actions: ['updateAnswerHistory'],
            },
            ANSWER_QUESTION: {
              actions: ['answerQuestion'],
            },
            APPEND_CONTENT: {
              actions: ['appendContent'],
            },
          },
        },
      },
    },
    {
      actions: {
        goToNextContent: assign({
          currentIndex: (ctx) => ctx.currentIndex + 1,
        }),
        goToPreviousContent: assign({
          currentIndex: (ctx) => ctx.currentIndex - 1,
        }),
        answerQuestion: assign({
          selectedAnswer: (_, event) => event.selectedAnswer,
        }),
        resetSelectedAnswer: assign({
          selectedAnswer: (_) => null,
        }),
        updateAnswerHistory: assign({
          answerHistory: (ctx, event) => event.answerHistory ?? ctx.answerHistory,
        }),
        appendContent: assign({
          contents: (ctx, evt) => [...ctx.contents, ...evt.content],
        }),
      },
      guards: guards.getGuards<TestNode<TContent, TAnswerContent>, TContent, TAnswerContent, TSelectedAnswer>(),
    }
  )
}

// function invokePingMachine(type: string) {
//   // TODO: create const/enum for invoked/spawned machines
//   const invokedId = `ping-machine`
//   return {
//     invoke: {
//       id: invokedId,
//       src: (ctx: ContentNavigatorMachineContext) =>
//         // TODO: set proper ID
//         pongMachineFactory(`content-${ctx.currentIndex}`, type),
//     },
//     exit: [send('COMPLETED', { to: invokedId })],
//   }
// }
